<template>
  <div class="container mx-auto px-4 py-6" v-if="order">
    <!-- Шапка досье -->
    <div class="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 mb-8">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-3xl font-bold text-slate-800 mb-2">
            Производственное досье #{{ order.tasks[0]?.id }}
          </h1>
          <div class="flex items-center space-x-4">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  :class="getStatusBadgeClass(order.tasks[0]?.status)">
              {{ getStatusLabel(order.tasks[0]?.status) }}
            </span>
          </div>
        </div>
        <button
          @click="goBackToSmk"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Назад
        </button>
      </div>

      <!-- Верхняя строка: Заказ, Дата -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div class="flex items-center space-x-3">
          <div class="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
          <div>
            <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold">Заказ</div>
            <div class="text-lg font-semibold text-slate-800">№ {{ order.order?.number || order.id }}</div>
          </div>
        </div>
        <div class="flex items-center space-x-3">
          <div class="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
          <div>
            <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold">Дата заказа</div>
            <div class="text-lg font-semibold text-slate-800">{{ formatDate(order.order?.created_at || order.created_at) }}</div>
          </div>
        </div>
      </div>

      <!-- Нижняя строка: Продукция, Серийный номер -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="flex items-start space-x-3">
          <div class="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
          <div class="flex-1">
            <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold mb-1">Продукция</div>
            <div class="text-lg font-semibold text-slate-800">
              <span :class="{ 'line-through text-gray-500': (order.order_product?.product || order.product)?.deleted_at }">
                {{ order.order_product?.product?.name || order.product?.name || 'Не указано' }}
              </span>
              <span v-if="(order.order_product?.product || order.product)?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
            </div>
          </div>
        </div>
        <div class="flex items-center space-x-3">
          <div class="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
          <div>
            <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold">Серийный номер</div>
            <div class="text-lg font-semibold text-slate-800">{{ order.tasks[0]?.batch_products?.[0]?.serial_number || 'N/A' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Информация по заданию -->
    <div class="space-y-6">
      <div v-for="(task, taskIndex) in order.tasks" :key="taskIndex"
           class="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div class="p-8">
          <!-- Кнопки печати по шагам операций -->
          <div class="mb-6">
            <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <div class="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
              Документы для печати
            </h5>
            <div class="space-y-3">
              <div v-for="(operation, opIndex) in task.operations.slice().sort((a, b) => (a.operation?.sort || 0) - (b.operation?.sort || 0))" :key="opIndex">
                <div v-if="getPrintStepsFromOperation(operation).length > 0" class="bg-white rounded-lg border border-slate-200 p-4">
                  <div class="text-sm font-medium text-slate-600 mb-3">
                    {{ operation.operation?.name || 'Операция без названия' }}
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="step in getPrintStepsFromOperation(operation)"
                      :key="step.id"
                      @click.prevent="printOperationStep(step, task)"
                      :disabled="!task.batch_products?.length"
                      class="inline-flex items-center px-3 py-2 bg-blue-600 border border-transparent
                        rounded-md font-semibold text-xs text-white uppercase tracking-widest
                        hover:bg-blue-700 focus:bg-blue-700 active:bg-blue-900 focus:outline-none
                        focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out
                        duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      {{ step.sort }}. {{ getStepDocumentNames(step) }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="getAllPrintStepsFromTask(task).length === 0" class="text-sm text-slate-500 italic">
                Нет шагов печати для данной задачи
              </div>
            </div>
          </div>

          <!-- Упаковочные листы -->
          <div v-if="getDonePackagingOperations(task).length > 0" class="mb-6">
            <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <div class="w-2 h-2 bg-indigo-400 rounded-full mr-2"></div>
              Упаковочные листы
            </h5>
            <div class="space-y-3">
              <div v-for="(operation, opIndex) in getDonePackagingOperations(task)" :key="'packing-' + opIndex">
                <div class="bg-white rounded-lg border border-slate-200 p-4">
                  <div class="text-sm font-medium text-slate-600 mb-3">
                    {{ operation.operation?.name || 'Операция без названия' }}
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      @click.prevent="printPackingDocument('order', task)"
                      class="inline-flex items-center px-3 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Упаковочный лист заказа
                    </button>
                    <button
                      @click.prevent="printPackingDocument('place', task)"
                      class="inline-flex items-center px-3 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Упаковочные листы по местам
                    </button>
                    <button
                      @click.prevent="printPackingDocument('labels', task)"
                      class="inline-flex items-center px-3 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Транспортные этикетки
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Раскрывашка "Все использованные компоненты" -->
          <div v-if="task.task_products && task.task_products.length > 0" class="mb-6">
            <button
              @click="toggleOperationProducts(task.id)"
              class="flex items-center w-full text-left mb-4 group">
              <svg
                class="w-5 h-5 text-slate-500 mr-2 transition-transform duration-200"
                :class="{ 'rotate-90': expandedOperationProducts.includes(task.id) }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
              <h5 class="text-lg font-semibold text-slate-800 flex items-center group-hover:text-blue-600 transition-colors">
                <div class="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
                Все использованные компоненты ({{ task.task_products.length }})
              </h5>
            </button>

            <div v-if="expandedOperationProducts.includes(task.id)" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div v-for="(product, productIndex) in task.task_products" :key="'all-' + productIndex"
                   class="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
                   :class="product.is_overpick ? 'border-amber-300 bg-amber-50' : 'border-slate-200'">
                <div class="space-y-3">
                  <div>
                    <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Наименование</div>
                    <div class="text-sm font-bold text-slate-800">
                      <span :class="{ 'line-through text-gray-500': (product.storehouse_product?.batch_product?.product || product.product)?.deleted_at }">
                        {{ product.storehouse_product?.batch_product?.product?.name || product.product?.name || 'N/A' }}
                      </span>
                      <span v-if="(product.storehouse_product?.batch_product?.product || product.product)?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                      <span
                        v-if="product.is_overpick"
                        class="ml-2 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold whitespace-nowrap"
                        title="Взято больше, чем требуется по спецификации"
                      >
                        Перерасход
                      </span>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Накладная</div>
                      <div class="font-semibold text-slate-700">
                        {{ product.storehouse_product?.batch_product?.receipt_invoice_product?.receipt_invoice?.number
                          ? product.storehouse_product.batch_product.receipt_invoice_product.receipt_invoice.number + ' (' + product.storehouse_product.batch_product.receipt_invoice_product.receipt_invoice.id + ')'
                          : 'N/A' }}
                      </div>
                    </div>
                    <div v-if="product.storehouse_product?.batch_product">
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Серийный №</div>
                      <div class="font-semibold text-slate-700">
                        {{ product.storehouse_product?.batch_product?.serial_number ?? 'N/A' }}
                      </div>
                    </div>
                    <div v-else>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Количество</div>
                      <div class="font-semibold text-slate-700">
                        {{ product?.count }}
                      </div>
                    </div>
                    <div>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Партия</div>
                      <div class="font-semibold text-slate-700">
                        {{ product.storehouse_product?.batch_product?.batch_manufacture?.number ?? 'N/A' }}
                      </div>
                    </div>
                    <div>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Поставщик</div>
                      <div class="font-semibold text-slate-700">
                        {{ product.storehouse_product?.batch_product?.batch_manufacture?.supplier?.name
                          ?? product.storehouse_product?.batch_product?.receipt_invoice_product?.receipt_invoice?.supplier?.name
                          ?? 'N/A' }}
                      </div>
                    </div>
                    <div>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Дата производства</div>
                      <div class="font-semibold text-slate-700">
                        {{ formatDate(product.storehouse_product?.batch_product?.batch_manufacture?.manufacture_date) }}
                      </div>
                    </div>
                    <div>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Срок годности</div>
                      <div class="font-semibold text-slate-700">
                        {{ formatDate(product.storehouse_product?.batch_product?.batch_manufacture?.expiry_date) }}
                      </div>
                    </div>
                    <div>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Поступление на склад</div>
                      <div class="font-semibold text-slate-700">
                        {{ formatDate(product.storehouse_product?.created_at) }}
                      </div>
                    </div>
                    <div>
                      <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Ячейка</div>
                      <div class="font-semibold text-slate-700">
                        {{ getStorehouseCell(product.storehouse_product) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-8">
            <div v-for="(operation, opIndex) in task.operations.slice().sort((a, b) => (a.operation?.sort || 0) - (b.operation?.sort || 0))"
                 :key="opIndex"
                 class="relative">

              <!-- Линия соединения операций -->
              <div v-if="opIndex < task.operations.length - 1"
                   class="absolute left-6 top-16 w-0.5 h-full bg-gradient-to-b from-blue-300 to-transparent z-0">
              </div>

              <div class="relative bg-slate-50 rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300">
                <!-- Иконка операции -->
                <div class="absolute -left-3 top-6 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                  <div class="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <span class="text-blue-600 font-bold text-sm">{{ opIndex + 1 }}</span>
                  </div>
                </div>

                <div class="ml-12">
                  <!-- Информация об операции -->
                  <div class="flex items-start justify-between mb-6">
                    <div class="flex-1">
                      <div class="flex items-center space-x-3 mb-3">
                        <h4 class="text-xl font-bold text-slate-800">
                          {{ operation.operation?.name || 'Операция без названия' }}
                        </h4>
                        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                              :class="getOperationStatusClass(operation.status)">
                          {{ getStatusLabel(operation.status) }}
                        </span>
                      </div>

                      <!-- Временные рамки -->
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        <div class="flex items-center space-x-2">
                          <div class="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <div>
                            <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold">Начало</div>
                            <div class="text-sm font-semibold text-slate-700">
                              {{ operation.fact_start_datetime ? getDateTime(operation.fact_start_datetime) : 'Не начато' }}
                            </div>
                          </div>
                        </div>
                        <div class="flex items-center space-x-2">
                          <div class="w-2 h-2 bg-green-400 rounded-full"></div>
                          <div>
                            <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold">Завершение</div>
                            <div class="text-sm font-semibold text-slate-700">
                              {{ operation.fact_end_datetime ? getDateTime(operation.fact_end_datetime) : 'Не завершено' }}
                            </div>
                          </div>
                        </div>
                        <div class="flex items-center space-x-2">
                          <div class="w-2 h-2 bg-purple-400 rounded-full"></div>
                          <div>
                            <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold">Длительность</div>
                            <div class="text-sm font-semibold text-slate-700">
                              {{ getDuration(operation.fact_start_datetime, operation.fact_end_datetime) }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Рабочий центр и ответственный -->
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div class="bg-white rounded-lg p-4 border border-slate-200">
                          <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Рабочий центр</div>
                          <div class="text-sm font-semibold text-slate-800" v-if="getWorkCenterLabel(operation)">
                            {{ getWorkCenterLabel(operation) }}
                          </div>
                          <div class="text-sm text-slate-500 italic" v-else>
                            Не назначен
                          </div>
                        </div>
                        <div class="bg-white rounded-lg p-4 border border-slate-200">
                          <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Ответственный</div>
                          <div class="text-sm font-semibold text-slate-800" v-if="operation.user">
                            {{ operation.user.first_name }} {{ operation.user.last_name }}
                          </div>
                          <div class="text-sm text-slate-500 italic" v-else>
                            Не назначен
                          </div>
                        </div>
                      </div>

                      <!-- Требуемые компоненты -->
                      <div v-if="isPickingOperation(operation) && getRequiredComponents(operation).length > 0" class="mb-6 bg-white rounded-lg border border-slate-200 p-4">
                        <div class="flex items-center justify-between mb-3">
                          <h5 class="text-md font-semibold text-slate-700 flex items-center">
                            <div class="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
                            Требуемые компоненты
                          </h5>
                        </div>
                        <table class="w-full border-collapse text-sm">
                          <thead class="bg-slate-50">
                            <tr>
                              <th class="px-3 py-2 text-left text-slate-600">Наименование</th>
                              <th class="px-3 py-2 text-left text-slate-600">Требуется по спецификации</th>
                              <th class="px-3 py-2 text-left text-slate-600">Взято фактически</th>
                              <th class="px-3 py-2 text-left text-slate-600">Позиции</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="comp in getRequiredComponents(operation)" :key="comp.row_key"
                                class="border-b border-slate-100"
                                :class="{ 'bg-amber-50': comp.has_overpick }">
                              <td class="px-3 py-2 font-medium text-slate-800">
                                {{ comp.product_name }}
                                <span
                                  v-if="comp.has_overpick"
                                  class="ml-2 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold whitespace-nowrap"
                                  title="Взято больше, чем требуется по спецификации"
                                >
                                  Перерасход
                                </span>
                              </td>
                              <td class="px-3 py-2 text-slate-600">{{ formatQuantity(comp.required_quantity) }}</td>
                              <td class="px-3 py-2" :class="comp.has_overpick ? 'text-amber-700 font-semibold' : 'text-slate-600'">
                                {{ formatQuantity(comp.picked_quantity) }}
                              </td>
                              <td class="px-3 py-2">
                                <div v-if="comp.planned_serials.length > 0" class="space-y-1">
                                  <div v-for="(planned, pIdx) in comp.planned_serials" :key="pIdx" class="flex items-center space-x-2">
                                    <span class="text-sm text-slate-700">{{ planned.label }}</span>
                                    <span
                                      v-if="planned.is_overpick"
                                      class="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold whitespace-nowrap"
                                      title="Взято больше, чем требуется по спецификации"
                                    >
                                      Перерасход
                                    </span>
                                  </div>
                                </div>
                                <span v-else class="text-slate-400">—</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Компоненты по шагам операции -->
                      <template v-for="(step, stepIndex) in (operation.steps || [])" :key="'step-' + stepIndex">
                        <div v-if="getStepTaskProducts(step, operation).length > 0" class="mb-6">
                          <h5 class="text-md font-semibold text-slate-700 mb-3 flex items-center">
                            <div class="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                            {{ step.operation_step?.name || ('Шаг ' + (stepIndex + 1)) }}
                          </h5>
                          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div v-for="(product, productIndex) in getStepTaskProducts(step, operation)" :key="'step-prod-' + productIndex"
                                 class="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
                                 :class="product.is_overpick ? 'border-amber-300 bg-amber-50' : 'border-slate-200'">
                              <div class="space-y-3">
                                <div>
                                  <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Наименование</div>
                                  <div class="text-sm font-bold text-slate-800">
                                    <span :class="{ 'line-through text-gray-500': (product.storehouse_product?.batch_product?.product || product.product)?.deleted_at }">
                                      {{ product.storehouse_product?.batch_product?.product?.name || product.product?.name || 'N/A' }}
                                    </span>
                                    <span v-if="(product.storehouse_product?.batch_product?.product || product.product)?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                                    <span
                                      v-if="product.is_overpick"
                                      class="ml-2 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold whitespace-nowrap"
                                      title="Взято больше, чем требуется по спецификации"
                                    >
                                      Перерасход
                                    </span>
                                  </div>
                                </div>
                                <div class="grid grid-cols-2 gap-3 text-xs">
                                  <div>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Накладная</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ product.storehouse_product?.batch_product?.receipt_invoice_product?.receipt_invoice?.number
                                        ? product.storehouse_product.batch_product.receipt_invoice_product.receipt_invoice.number + ' (' + product.storehouse_product.batch_product.receipt_invoice_product.receipt_invoice.id + ')'
                                        : 'N/A' }}
                                    </div>
                                  </div>
                                  <div v-if="product.storehouse_product?.batch_product">
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Серийный №</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ product.storehouse_product?.batch_product?.serial_number ?? 'N/A' }}
                                    </div>
                                  </div>
                                  <div v-else>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Количество</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ product?.count }}
                                    </div>
                                  </div>
                                  <div>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Партия</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ product.storehouse_product?.batch_product?.batch_manufacture?.number ?? 'N/A' }}
                                    </div>
                                  </div>
                                  <div>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Поставщик</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ product.storehouse_product?.batch_product?.batch_manufacture?.supplier?.name
                                        ?? product.storehouse_product?.batch_product?.receipt_invoice_product?.receipt_invoice?.supplier?.name
                                        ?? 'N/A' }}
                                    </div>
                                  </div>
                                  <div>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Дата производства</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ formatDate(product.storehouse_product?.batch_product?.batch_manufacture?.manufacture_date) }}
                                    </div>
                                  </div>
                                  <div>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Срок годности</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ formatDate(product.storehouse_product?.batch_product?.batch_manufacture?.expiry_date) }}
                                    </div>
                                  </div>
                                  <div>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Поступление на склад</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ formatDate(product.storehouse_product?.created_at) }}
                                    </div>
                                  </div>
                                  <div>
                                    <div class="text-slate-500 uppercase tracking-wide font-semibold mb-1">Ячейка</div>
                                    <div class="font-semibold text-slate-700">
                                      {{ getStorehouseCell(product.storehouse_product) }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Фотографии -->
                      <div v-if="operation.photos && operation.photos.length > 0" class="mb-4">
                        <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                          <div class="w-2 h-2 bg-pink-400 rounded-full mr-2"></div>
                          Фотофиксация выполнения
                        </h5>

                        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                          <div v-for="(photo, photoIndex) in operation.photos" :key="photoIndex"
                               class="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                               @click="openPhotosModal(operation.id, photoIndex)">
                            <img :src="photo.path" alt="Фото работы"
                                 class="w-full h-20 object-cover"/>
                            <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                              <div class="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                                </svg>
                              </div>
                            </div>
                            <div class="absolute top-1 right-1 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                              {{ photoIndex + 1 }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно для просмотра фотографий -->
    <div v-if="isPhotosModalVisible"
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm"
         @click="closePhotosModal">
      <div class="bg-white rounded-2xl shadow-2xl w-[95%] max-w-6xl max-h-[90vh] overflow-hidden"
           @click.stop>
        <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
          <div class="flex justify-between items-center">
            <div>
              <h2 class="text-xl font-bold text-white">Фотофиксация операции</h2>
              <p class="text-slate-300 text-sm" v-if="operationPhotos.length > 0">
                Фото {{ currentPhotoIndex + 1 }} из {{ operationPhotos.length }}
              </p>
            </div>
            <button @click="closePhotosModal"
                    class="text-slate-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="p-6">
          <div v-if="photosLoading" class="flex justify-center items-center h-96">
            <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          </div>

          <div v-else-if="operationPhotos.length > 0" class="space-y-6">
            <div class="relative bg-slate-100 rounded-xl overflow-hidden">
              <img :src="operationPhotos[currentPhotoIndex].path"
                   alt="Фото работы"
                   class="w-full max-h-96 object-contain mx-auto"/>

              <button v-if="operationPhotos.length > 1"
                      @click="previousPhoto"
                      class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              <button v-if="operationPhotos.length > 1"
                      @click="nextPhoto"
                      class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>

            <div class="bg-slate-50 rounded-lg p-4">
              <div class="flex items-center justify-center">
                <div class="text-center">
                  <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                    Дата и время съемки
                  </div>
                  <div class="text-lg font-semibold text-slate-800">
                    {{ formatPhotoDateTime(operationPhotos[currentPhotoIndex].created_at) }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="operationPhotos.length > 1" class="space-y-2">
              <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                Все фотографии ({{ operationPhotos.length }})
              </div>
              <div class="flex space-x-2 overflow-x-auto pb-2">
                <div v-for="(photo, index) in operationPhotos" :key="index"
                     class="flex-shrink-0 cursor-pointer rounded-lg overflow-hidden border-2 transition-all"
                     :class="index === currentPhotoIndex ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-slate-300'"
                     @click="currentPhotoIndex = index">
                  <img :src="photo.path" alt="Превью" class="w-16 h-16 object-cover"/>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-12">
            <svg class="mx-auto h-16 w-16 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <p class="text-lg font-medium text-slate-700 mb-2">Фотографии не найдены</p>
            <p class="text-slate-500">Для данной операции фотофиксация не проводилась</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Загрузка -->
  <div v-else class="container mx-auto px-4 py-6 flex items-center justify-center">
    <div class="text-center">
      <div class="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
      <p class="text-xl font-semibold text-slate-700">Загрузка производственного досье...</p>
      <p class="text-slate-500 mt-2">Получаем информацию о производстве изделия</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from "moment/moment.js";

export default {
  data() {
    return {
      order: null,
      loading: false,
      isPhotosModalVisible: false,
      operationPhotos: [],
      photosLoading: false,
      currentOperationId: null,
      currentPhotoIndex: 0,
      expandedOperationProducts: [],
    };
  },
  methods: {
    // Рабочий центр операции: пост из спецификации известен сразу после создания задания,
    // стол добавляется, когда операцию взяли в работу
    getWorkCenterLabel(operation) {
      const post = operation.operation?.workshop_post?.name;
      const unit = operation.workshop_post_unit?.name;

      if (post && unit) return `${post} — ${unit}`;

      return post || unit || '';
    },
    goBackToSmk() {
      const moduleId = this.$route.params.id;
      if (moduleId) {
        this.$router.push({ name: 'SmkWorkPlace', params: { id: moduleId } });
      } else {
        this.$router.back();
      }
    },
    async fetchDossier(taskId) {
      try {
        this.loading = true;
        const response = await axios.post('/api/productionOrder/getProductionOrderDossier', { task_id: taskId });
        this.order = response.data.data;

        if (this.order && this.order.tasks) {
          for (const task of this.order.tasks) {
            for (const operation of task.operations) {
              await this.fetchOperationPhotos(operation);
            }
          }
        }
      } catch (error) {
        console.error('Ошибка при загрузке производственного досье:', error);
        const msg = error.response?.data?.message || 'Не удалось загрузить производственное досье';
        alert(msg);
      } finally {
        this.loading = false;
      }
    },

    // Требуемые компоненты операции: строка на каждый компонент шага комплектования —
    // и штучный, и количественный. Рядом с потребностью по спецификации показывается
    // фактически взятое; строки, где взяли больше спецификации, помечаются.
    // Логика повторяет карточку заказа (OrderShow.vue), иначе досье и карточка разойдутся.
    getRequiredComponents(operation) {
      const rows = [];
      (operation.steps || [])
        .filter(s => s.operation_step?.type === 'picking')
        .forEach((pickingStep, stepIdx) => {
          pickingStep.operation_step?.components?.forEach(component => {
            if (!component.product?.id) return;
            rows.push({
              row_key: `${pickingStep.id ?? stepIdx}-${component.id}`,
              component_id: component.id,
              product_id: component.product.id,
              product_name: component.product.name,
              product: component.product,
              is_single: !!component.is_single,
              required_quantity: Number(component.count) || 0,
              planned_serials: [],
              picked_quantity: 0,
              has_overpick: false,
            });
          });
        });

      if (rows.length === 0) return [];

      this.fillRequiredComponentsFact(operation, rows);

      return rows;
    },

    // Раскладывает запланированное и взятое по строкам компонентов.
    // Взятое с привязкой к компоненту шага ложится в свою строку; взятое раньше,
    // когда привязки ещё не было, относится к первой строке того же продукта —
    // иначе старые комплектования перестали бы отображаться вовсе.
    fillRequiredComponentsFact(operation, rows) {
      const rowsByComponent = new Map();
      rows.forEach(row => {
        if (row.component_id && !rowsByComponent.has(row.component_id)) {
          rowsByComponent.set(row.component_id, row);
        }
      });

      (operation.task_products || []).forEach(taskProduct => {
        // task_products приходят по всей задаче — позиции чужих операций отсеиваем
        if (taskProduct.operation_id && operation.id && taskProduct.operation_id !== operation.id) return;

        const productId = taskProduct.product_id || taskProduct.storehouse_product?.product_id;
        const row = rowsByComponent.get(taskProduct.operation_step_component_id)
          || rows.find(r => r.product_id === productId);
        if (!row) return;

        const bp = taskProduct.storehouse_product?.batch_products?.[0];
        const quantity = Number(taskProduct.count) || 0;
        const isPicked = !!taskProduct.is_picked;

        if (isPicked) {
          row.picked_quantity = Math.round((row.picked_quantity + quantity) * 1000) / 1000;
          if (taskProduct.is_overpick) row.has_overpick = true;
        }

        let label;
        if (bp?.serial_number) {
          label = bp.serial_number;
        } else if (row.is_single) {
          label = 'Без S/N';
        } else {
          label = (isPicked ? 'Взято ' : 'Запланировано ') + this.formatQuantity(quantity);
        }

        row.planned_serials.push({
          serial_number: bp?.serial_number || null,
          task_product_id: taskProduct.id,
          is_picked: isPicked,
          is_overpick: !!taskProduct.is_overpick,
          label,
        });
      });
    },

    toggleOperationProducts(operationId) {
      const index = this.expandedOperationProducts.indexOf(operationId);
      if (index === -1) {
        this.expandedOperationProducts.push(operationId);
      } else {
        this.expandedOperationProducts.splice(index, 1);
      }
    },

    getStepTaskProducts(step, operation) {
      const componentProductIds = (step.operation_step?.components || [])
        .map(c => c.product?.id)
        .filter(Boolean);

      if (componentProductIds.length === 0) return [];

      return (operation.task_products || []).filter(tp => {
        // task_products приходят по всей задаче — позиции чужих операций отсеиваем.
        // Без этого взятое на комплектовании показывается и в блоках операций, которые
        // ещё не начинались, вместе с пометкой перерасхода (AND-213)
        if (tp.operation_id && operation.id && tp.operation_id !== operation.id) return false;

        const tpProductId = tp.storehouse_product?.batch_product?.product_id
                         || tp.product?.id;
        return componentProductIds.includes(tpProductId);
      });
    },

    isPickingOperation(operation) {
      return (operation.steps || []).some(s => s.operation_step?.type === 'picking');
    },

    getPrintStepsFromOperation(operation) {
      if (!operation.operation?.steps) return [];
      return operation.operation.steps.filter(step =>
        step.type === 'print' &&
        step.documents &&
        step.documents.length > 0
      ).sort((a, b) => (a.sort || 0) - (b.sort || 0));
    },

    getAllPrintStepsFromTask(task) {
      const printSteps = [];
      task.operations.forEach(operation => {
        printSteps.push(...this.getPrintStepsFromOperation(operation));
      });
      return printSteps;
    },

    getStepDocumentNames(step) {
      if (!step.documents || !step.documents.length) return 'Нет документов';
      return step.documents.map(doc => doc.name).join(', ');
    },

    getPackagingOperations(task) {
      if (!task.operations) return [];
      return task.operations.filter(op =>
        op.operation?.steps?.some(step => step.type === 'packaging')
      );
    },

    getDonePackagingOperations(task) {
      return this.getPackagingOperations(task).filter(op => op.status === 'done');
    },

    async printPackingDocument(type, task) {
      const urls = {
        order: '/api/packingPlace/printPackingListOrder',
        place: '/api/packingPlace/printPackingListPlace',
        labels: '/api/packingPlace/printTransportLabels',
      };

      try {
        const response = await axios.get(urls[type], {
          params: { production_task_id: task.id },
        });
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('Разрешите всплывающие окна');
          return;
        }
        printWindow.document.write(response.data);
        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => printWindow.close(), 1000);
          }, 500);
        };
      } catch (error) {
        console.error('Ошибка печати:', error);
        alert('Ошибка печати: ' + (error.response?.data?.message || error.message));
      }
    },

    async printOperationStep(operationStep, task) {
      if (!operationStep.documents || !operationStep.documents.length) {
        alert('Для данного шага не найдены документы для печати');
        return;
      }

      const batchProduct = task.batch_products?.[0];
      const productId = batchProduct?.product?.id
        || this.order?.order_product?.product?.id
        || this.order?.product?.id;

      if (!productId) {
        alert('Не найдены данные продукции для печати');
        return;
      }

      const operation = task.operations?.find(op => op.operation?.steps?.some(s => s.id === operationStep.id));

      const taskData = {
        product_id: productId,
        batch_product_id: batchProduct?.id || null,
        production_task_operation_id: operation?.id ?? null,
        operation_step_id: operationStep.id ?? null,
      };

      for (const document of operationStep.documents) {
        try {
          await this.printDocumentFromStep(operationStep, document, taskData);
        } catch (error) {
          console.error(`Ошибка печати документа ${document.name}:`, error);
        }
      }
    },

    async printDocumentFromStep(operationStep, document, taskData) {
      if (!document?.id) {
        alert('Документ не содержит id');
        return;
      }
      if (!taskData?.batch_product_id) {
        alert('Не найден batch_product_id для печати');
        return;
      }

      try {
        const response = await axios.post('/api/documents/render', {
          document_id: document.id,
          batch_product_id: taskData.batch_product_id,
          operation_step_id: taskData.operation_step_id ?? operationStep?.id,
          production_task_operation_id: taskData.production_task_operation_id,
        });

        const printWindow = window.open('', '_blank');
        if (!printWindow) throw new Error('Разрешите всплывающие окна');

        printWindow.document.write(response.data.html);
        printWindow.document.close();

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => printWindow.close(), 1000);
          }, 500);
        };
      } catch (error) {
        console.error(`Ошибка печати ${document.name}:`, error);
        alert(`Ошибка: ${error.message}`);
      }
    },

    async fetchOperationPhotos(operation) {
      try {
        const response = await axios.post(`/api/productionOperation/photos`, { operationId: operation.id });
        if (response.data.success) {
          operation.photos = response.data.photos.map(photo => ({
            path: `/${photo.path}`,
            created_at: photo.created_at,
          }));
        } else {
          console.error('Ошибка при получении фотографий:', response.data.message);
          operation.photos = [];
        }
      } catch (error) {
        console.error('Ошибка при запросе фотографий:', error);
        operation.photos = [];
      }
    },

    getDateTime(date) {
      if (!date) return 'N/A';
      return moment(date).local().format('DD.MM.YYYY HH:mm');
    },

    formatQuantity(val) {
      if (val == null || val === undefined) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },

    getUnitLabel(product) {
      return product?.unit_short_name || product?.unit?.short_name || 'шт';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const momentDate = moment(date);
      if (!momentDate.isValid()) return 'N/A';
      return momentDate.local().format('DD.MM.YYYY');
    },

    formatPhotoDateTime(date) {
      if (!date) return 'Дата неизвестна';
      return moment(date).local().format('DD.MM.YYYY в HH:mm:ss');
    },

    getDuration(start, end) {
      if (!start || !end) return 'N/A';
      const duration = moment.duration(moment(end).diff(moment(start)));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();
      if (hours > 0) {
        return `${hours}ч ${minutes}мин ${seconds}сек`;
      }
      if (minutes > 0) {
        return `${minutes}мин ${seconds}сек`;
      }
      return `${seconds}сек`;
    },

    getStatusLabel(status) {
      switch (status) {
        case 'new': return 'Очередь';
        case 'work': return 'В работе';
        case 'done': return 'Выполнено';
        default: return 'Неизвестно';
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'new': return 'bg-yellow-100 text-yellow-800';
        case 'work': return 'bg-blue-100 text-blue-800';
        case 'done': return 'bg-green-100 text-green-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    },

    getTaskStatusClass(status) {
      switch (status) {
        case 'new': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
        case 'work': return 'bg-blue-100 text-blue-800 border border-blue-200';
        case 'done': return 'bg-green-100 text-green-800 border border-green-200';
        default: return 'bg-gray-100 text-gray-800 border border-gray-200';
      }
    },

    getOperationStatusClass(status) {
      switch (status) {
        case 'new': return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
        case 'work': return 'bg-blue-50 text-blue-700 border border-blue-200';
        case 'done': return 'bg-green-50 text-green-700 border border-green-200';
        default: return 'bg-gray-50 text-gray-700 border border-gray-200';
      }
    },

    async openPhotosModal(operationId, photoIndex = 0) {
      this.currentOperationId = operationId;
      this.currentPhotoIndex = photoIndex;
      this.isPhotosModalVisible = true;
      this.photosLoading = true;

      const operation = this.findOperationById(operationId);

      if (operation && operation.photos && operation.photos.length > 0) {
        this.operationPhotos = operation.photos;
      } else {
        try {
          const response = await axios.post(`/api/productionOperation/photos`, { operationId });
          if (response.data.success) {
            this.operationPhotos = response.data.photos.map(photo => ({
              path: `/${photo.path}`,
              created_at: photo.created_at,
            }));
          } else {
            console.error('Ошибка при получении фотографий:', response.data.message);
            this.operationPhotos = [];
          }
        } catch (error) {
          console.error('Ошибка при запросе фотографий:', error);
          this.operationPhotos = [];
        }
      }
      this.photosLoading = false;
    },

    closePhotosModal() {
      this.isPhotosModalVisible = false;
      this.operationPhotos = [];
      this.currentPhotoIndex = 0;
    },

    nextPhoto() {
      if (this.currentPhotoIndex < this.operationPhotos.length - 1) {
        this.currentPhotoIndex++;
      } else {
        this.currentPhotoIndex = 0;
      }
    },

    previousPhoto() {
      if (this.currentPhotoIndex > 0) {
        this.currentPhotoIndex--;
      } else {
        this.currentPhotoIndex = this.operationPhotos.length - 1;
      }
    },

    findOperationById(operationId) {
      if (!this.order || !this.order.tasks) return null;
      for (const task of this.order.tasks) {
        for (const operation of task.operations) {
          if (operation.id === operationId) {
            return operation;
          }
        }
      }
      return null;
    },

    getStorehouseCell(storehouseProduct) {
      if (!storehouseProduct) return 'N/A';
      return storehouseProduct.storehouse_cell?.structure_item?.full_path || 'N/A';
    }
  },

  mounted() {
    const taskId = this.$route.params.taskId;
    this.fetchDossier(taskId);

    document.addEventListener('keydown', (e) => {
      if (this.isPhotosModalVisible && this.operationPhotos.length > 1) {
        if (e.key === 'ArrowLeft') {
          this.previousPhoto();
        } else if (e.key === 'ArrowRight') {
          this.nextPhoto();
        } else if (e.key === 'Escape') {
          this.closePhotosModal();
        }
      }
    });
  }
};
</script>

<style scoped>
.transition-all {
  transition: all 0.3s ease;
}

.overflow-x-auto::-webkit-scrollbar {
  height: 4px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.fixed.inset-0 > div {
  animation: fadeIn 0.2s ease-out;
}
</style>
